import React, { ReactNode, useEffect, useState } from 'react'
import { Button, Input, Popconfirm, Tooltip, Typography } from 'antd'
import {
  CopyOutlined,
  DeleteOutlined,
  PlusOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons'
import { actionsApi } from 'api/actions'
import { GetProductModal } from 'components/GetProductModal'
import { ICustomFeed, IFeedProduct, IFeedStats, TFeedType } from 'models/feeds'
import { IProductResponse } from 'models/product'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import { FeedProductsModal } from './FeedProductsModal'
import styles from './Feeds.module.scss'

const PLATFORMS: TFeedType[] = ['yandex', 'vk']

const PLATFORM_TITLE: Record<TFeedType, string> = {
  yandex: 'Яндекс фид',
  vk: 'VK фид'
}

// Адреса витрины, а не панели: фиды забирают Яндекс и VK с публичного домена
const MAIN_FEED_URL: Record<TFeedType, string> = {
  yandex: 'https://horsesmart.store/yandex-feed.xml',
  vk: 'https://horsesmart.store/vk-feed.xml'
}

const COMMON_CONDITIONS = [
  'статус «Опубликован»',
  'продавец подтверждён и не отключён',
  'остаток больше нуля',
  'есть хотя бы одно фото',
  'в названии нет «курит» — курительное каталоги не принимают'
]

const PLATFORM_CONDITIONS: Record<TFeedType, string[]> = {
  yandex: COMMON_CONDITIONS,
  vk: [
    ...COMMON_CONDITIONS,
    'подкатегория товара есть в справочнике',
    'название не пустеет после чистки от эмодзи'
  ]
}

const FEED_NAME_MAX_LENGTH = 100

export const ActionsFeeds: React.FC = () => {
  const [stats, setStats] = useState<IFeedStats | null>(null)
  const [isStatsLoading, setIsStatsLoading] = useState(true)
  const [customFeeds, setCustomFeeds] = useState<ICustomFeed[]>([])
  const [isCreating, setIsCreating] = useState(false)
  const [newFeedName, setNewFeedName] = useState('')
  // Товары фида, который дублируем: окно создания откроется с ними
  const [initialProducts, setInitialProducts] = useState<IFeedProduct[]>([])
  const [duplicatingFeedId, setDuplicatingFeedId] = useState<string | null>(
    null
  )
  const [viewedFeed, setViewedFeed] = useState<ICustomFeed | null>(null)

  useEffect(() => {
    const loadStats = async () => {
      try {
        const response = await actionsApi.getFeedStats()
        setStats(response.data)
      } catch {
        setStats(null)
      } finally {
        setIsStatsLoading(false)
      }
    }
    const loadCustomFeeds = async () => {
      try {
        const response = await actionsApi.getFeeds()
        setCustomFeeds(response.data)
      } catch (e) {
        showServerError(e)
      }
    }

    loadStats()
    loadCustomFeeds()
  }, [])

  const handleCopy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      showMessage('success', 'Ссылка скопирована', '')
    } catch {
      showMessage('error', 'Не удалось скопировать ссылку', '')
    }
  }

  // Ошибку не глушим: модалка покажет её и оставит выбранные товары
  const createFeed = async (products: Pick<IProductResponse, '_id'>[]) => {
    const response = await actionsApi.createFeed({
      name: newFeedName.trim(),
      product_ids: products.map((product) => product._id)
    })
    setCustomFeeds((prev) => [...prev, response.data])
    showMessage('success', `Фид «${response.data.name}» создан`, '')
  }

  const loadFeedProducts = async () => {
    const response = await actionsApi.getFeedProductIds()
    return response.data.map((_id) => ({ _id }))
  }

  const startCreating = () => {
    setInitialProducts([])
    setIsCreating(true)
  }

  const closeCreating = () => {
    setIsCreating(false)
    setNewFeedName('')
    setInitialProducts([])
  }

  const duplicateFeed = async (feed: ICustomFeed) => {
    try {
      setDuplicatingFeedId(feed._id)
      const response = await actionsApi.getFeedProducts(feed._id)
      setInitialProducts(response.data)
      setIsCreating(true)
    } catch (e) {
      showServerError(e)
    } finally {
      setDuplicatingFeedId(null)
    }
  }

  const deleteFeed = async (feed: ICustomFeed) => {
    try {
      await actionsApi.deleteFeed(feed._id)
      setCustomFeeds((prev) => prev.filter((item) => item._id !== feed._id))
      showMessage('success', `Фид «${feed.name}» удалён`, '')
    } catch (e) {
      showServerError(e)
    }
  }

  const getCountText = (platform: TFeedType) => {
    if (isStatsLoading) {
      return 'Товаров в фиде: считаем…'
    }
    if (!stats) {
      return 'Количество товаров получить не удалось'
    }
    return `Товаров в фиде: ${stats[platform]}`
  }

  const renderPlatform = (
    platform: TFeedType,
    url: string,
    conditions: string[],
    count: ReactNode
  ) => (
    <div key={platform} className={styles.platform}>
      <div className={styles.platformTitle}>
        <Typography.Title level={5} className={styles.title}>
          {PLATFORM_TITLE[platform]}
        </Typography.Title>
        <Tooltip
          title={
            <>
              Попадают товары, у которых:
              <ul className={styles.conditions}>
                {conditions.map((condition) => (
                  <li key={condition}>{condition}</li>
                ))}
              </ul>
            </>
          }
        >
          <QuestionCircleOutlined className={styles.hint} />
        </Tooltip>
      </div>
      <div className={styles.linkRow}>
        <Input readOnly value={url} className={styles.linkInput} />
        <Button type='default' onClick={() => handleCopy(url)}>
          Копировать
        </Button>
      </div>
      {count}
    </div>
  )

  return (
    <div className={styles.root}>
      <div className={styles.feed}>
        <div className={styles.titleRow}>
          <Typography.Title level={4} className={styles.title}>
            Общий фид
          </Typography.Title>
          <Button icon={<PlusOutlined />} onClick={startCreating}>
            Создать фид
          </Button>
        </div>
        {PLATFORMS.map((platform) =>
          renderPlatform(
            platform,
            MAIN_FEED_URL[platform],
            PLATFORM_CONDITIONS[platform],
            <Typography.Text strong>{getCountText(platform)}</Typography.Text>
          )
        )}
      </div>
      {customFeeds.map((feed) => (
        <div key={feed._id} className={styles.feed}>
          <div className={styles.titleRow}>
            <Typography.Title level={4} className={styles.title}>
              {feed.name}
            </Typography.Title>
            <div className={styles.feedActions}>
              <Button
                icon={<CopyOutlined />}
                loading={duplicatingFeedId === feed._id}
                onClick={() => duplicateFeed(feed)}
              >
                Дублировать
              </Button>
              <Popconfirm
                title='Удалить фид?'
                description='Обе ссылки перестанут работать.'
                okText='Удалить'
                cancelText='Отмена'
                onConfirm={() => deleteFeed(feed)}
              >
                <Button danger icon={<DeleteOutlined />}>
                  Удалить
                </Button>
              </Popconfirm>
            </div>
          </div>
          {PLATFORMS.map((platform) =>
            renderPlatform(
              platform,
              feed.platforms[platform].url,
              ['выбран при создании фида', ...PLATFORM_CONDITIONS[platform]],
              <div className={styles.countRow}>
                <Typography.Text strong>
                  Товаров в фиде: {feed.platforms[platform].offers_count} из{' '}
                  {feed.selected_count} выбранных
                </Typography.Text>
                <Button
                  type='link'
                  size='small'
                  onClick={() => setViewedFeed(feed)}
                >
                  Подробнее
                </Button>
              </div>
            )
          )}
        </div>
      ))}
      <GetProductModal
        visible={isCreating}
        title='Новый фид'
        saveText='Создать фид'
        closeConfirm='Закрыть без создания фида?'
        saveDisabled={!newFeedName.trim()}
        initialSelected={initialProducts}
        feedFilter
        loadInvertScope={loadFeedProducts}
        onSave={createFeed}
        onClose={closeCreating}
      >
        <div className={styles.nameField}>
          <Typography.Text strong>Название фида</Typography.Text>
          <Input
            size='large'
            value={newFeedName}
            maxLength={FEED_NAME_MAX_LENGTH}
            showCount
            placeholder='Например, «Фид для избранных продавцов»'
            onChange={(e) => setNewFeedName(e.target.value)}
          />
        </div>
      </GetProductModal>
      <FeedProductsModal
        feed={viewedFeed}
        onClose={() => setViewedFeed(null)}
      />
    </div>
  )
}
